import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faSync, faTimes, faTable, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { getApvTransactionReport } from '../../services/apvApi';
import './apv-transaction-report.css';

function ApvTransactionReportModal({ isOpen, onClose, contract }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Currency format helper
  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  useEffect(() => {
    if (isOpen && contract?.referenceNo) {
      setCurrentPage(1);
      fetchReport(contract.referenceNo);
    } else {
      setReportData(null);
      setError(null);
      setCurrentPage(1);
    }
  }, [isOpen, contract?.referenceNo]);

  const fetchReport = async (refNo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getApvTransactionReport(refNo);
      if (response && (response.Success || response.success)) {
        setReportData(response);
      } else {
        // Fallback local calculations if needed
        buildFallbackReport(contract);
      }
    } catch (err) {
      console.warn('[ApvTransactionReportModal] Error fetching report:', err);
      buildFallbackReport(contract);
    } finally {
      setLoading(false);
    }
  };

  const buildFallbackReport = (c) => {
    if (!c) return;
    const base = Number(c.baseAmount) || 0;
    const opt = Number(c.optionAmount) || 0;
    const cont = Number(c.contingencyAmount) || (base + opt) * 0.05;
    const total = Number(c.totalApv) || (base + opt + cont);
    const used = Number(c.usedCost) || 0;
    const avail = Math.max(0, total - used);

    setReportData({
      Success: true,
      ReferenceNo: c.referenceNo,
      Discipline: c.discipline || 'General',
      BaseAmount: base,
      BasePeriod: c.basePeriod || '-',
      OptionAmount: opt,
      OptionPeriod: c.optionPeriod || '-',
      ContingencyAmount: cont,
      TotalApv: total,
      TotalFeeUsed: used,
      AvailableBalance: avail,
      Vendors: c.vendors || [],
      Transactions: [],
      TotalConstructionCost: 0,
      TotalContingency: 0,
      TotalConstCostPlusContingency: 0
    });
  };

  // Export Table to CSV
  const handleExportCSV = () => {
    if (!reportData) return;
    const transactions = reportData.Transactions || reportData.transactions || [];

    const headers = [
      'No',
      'Description',
      'Construction Cost',
      'Contingency (5%)',
      'Const Cost + Contingency',
      'Total Fee',
      'Available Budget'
    ];

    const rows = transactions.map((t, idx) => [
      idx + 1,
      `"${(t.Description || t.description || '').replace(/"/g, '""')}"`,
      (t.ConstructionCost ?? t.constructionCost ?? 0).toFixed(2),
      (t.ContingencyAmount ?? t.contingencyAmount ?? 0).toFixed(2),
      (t.ConstCostPlusContingency ?? t.constCostPlusContingency ?? 0).toFixed(2),
      (t.TotalFee ?? t.totalFee ?? 0).toFixed(2),
      (t.AvailableBudget ?? t.availableBudget ?? 0).toFixed(2)
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `APV_Report_${reportData.ReferenceNo || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const transactions = reportData?.Transactions || reportData?.transactions || [];
  const totalItems = transactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstItem, indexOfLastItem);

  const refNo = reportData?.ReferenceNo || contract?.referenceNo || '-';
  const discipline = reportData?.Discipline || contract?.discipline || '-';
  const originalTotalBudget = (() => {
    if (reportData?.TotalApv != null) return Number(reportData.TotalApv);
    if (contract) {
      const sum = (Number(contract.baseAmount) || 0) + (Number(contract.optionAmount) || 0) + (Number(contract.contingencyAmount) || 0);
      if (sum > 0) return sum;
      if (contract.originalTotalApv != null) return Number(contract.originalTotalApv);
      if (contract.totalApv != null) return Number(contract.totalApv);
    }
    return 0;
  })();
  const availBalance = reportData?.AvailableBalance ?? contract?.balanceApv ?? originalTotalBudget;
  const totalUsed = reportData?.TotalFeeUsed ?? contract?.usedCost ?? 0;

  return (
    <div className="apv-report-backdrop" onClick={onClose}>
      <div className="apv-report-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="apv-report-header">
          <div className="apv-report-title-group">
            <h3>
              <FontAwesomeIcon icon={faTable} style={{ color: '#4f46e5' }} />
              APV Transaction & Calculation Report
              <span className="apv-ref-badge">{refNo}</span>
              <span className="apv-disc-badge">{discipline}</span>
            </h3>
          </div>
          <button className="apv-report-close-btn" onClick={onClose} title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="apv-report-body">
          {/* Top Meta Bar: Original Total Budget Label */}
          <div className="apv-report-meta-header">
            <div className="apv-budget-label-container">
              <span className="apv-budget-caption">Original Total Budget:</span>
              <span className="apv-budget-value">{formatCurrency(originalTotalBudget)}</span>
            </div>
          </div>

          {/* Transaction Calculation Table */}
          {loading ? (
            <div className="apv-report-loading">
              <FontAwesomeIcon icon={faSync} spin size="2x" style={{ color: '#4f46e5' }} />
              <span>Loading APV transaction calculation...</span>
            </div>
          ) : (
            <div className="apv-report-table-wrapper">
              <table className="apv-report-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>No</th>
                    <th>1. Description</th>
                    <th className="text-right">2. Construction Cost</th>
                    <th className="text-right">3. Contingency (5%)</th>
                    <th className="text-right">4. Const Cost + Contingency</th>
                    <th className="text-right">5. Total Fee</th>
                    <th className="text-right">6. Available Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.length > 0 ? (
                    currentTransactions.map((item, index) => {
                      const globalIdx = indexOfFirstItem + index;
                      const desc = item.Description || item.description || '-';
                      const constCost = item.ConstructionCost ?? item.constructionCost ?? 0;
                      const contingency = item.ContingencyAmount ?? item.contingencyAmount ?? 0;
                      const constPlusCont = item.ConstCostPlusContingency ?? item.constCostPlusContingency ?? 0;
                      const fee = item.TotalFee ?? item.totalFee ?? 0;
                      const avail = item.AvailableBudget ?? item.availableBudget ?? 0;

                      return (
                        <tr key={item.Id || item.id || globalIdx}>
                          <td style={{ textAlign: 'center', color: '#64748b' }}>{globalIdx + 1}</td>
                          <td>
                            <div style={{ fontWeight: '600', color: '#0f172a' }}>{desc}</div>
                            {item.VendorName && (
                              <small style={{ color: '#64748b' }}>
                                Vendor: {item.VendorName} {item.DateOfAward ? `• Award: ${item.DateOfAward}` : ''}
                              </small>
                            )}
                          </td>
                          <td className="text-right" style={{ fontWeight: '500' }}>
                            {formatCurrency(constCost)}
                          </td>
                          <td className="text-right" style={{ color: '#0284c7' }}>
                            {formatCurrency(contingency)}
                          </td>
                          <td className="text-right" style={{ fontWeight: '600' }}>
                            {formatCurrency(constPlusCont)}
                          </td>
                          <td className="text-right" style={{ fontWeight: '600', color: '#dc2626' }}>
                            {formatCurrency(fee)}
                          </td>
                          <td className="text-right" style={{ fontWeight: '700', color: '#16a34a' }}>
                            {formatCurrency(avail)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="apv-report-empty">
                        No linked LOA/PO projects/transactions deducting this APV yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Table Footer and Pagination */}
              {totalItems > 0 && (
                <div className="apv-table-footer" style={{ borderTop: '1px solid #dee2e6', background: '#f8f9fa' }}>
                  <div>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} records
                  </div>
                  {totalPages > 1 && (
                    <div className="apv-pagination">
                      <button 
                        className="apv-page-link" 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button 
                          key={i} 
                          className={`apv-page-link ${currentPage === i + 1 ? 'active' : ''}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button 
                        className="apv-page-link" 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="apv-report-footer">
          {/* <button 
            className="apv-btn-export" 
            onClick={handleExportCSV}
            disabled={loading || transactions.length === 0}
            title="Download CSV Control Sheet"
          >
            <FontAwesomeIcon icon={faFileDownload} />
            Export CSV
          </button> */}
          <button className="apv-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApvTransactionReportModal;
